import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Lock } from "lucide-react";
import Seo from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ASSETS, COMPANY } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("bnm_admin_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Admin Login — BNM Sustainable Energy Limited" description="Restricted area." />
      <section data-testid="admin-login-page" className="relative min-h-screen overflow-hidden bg-obsidian grid-lines-dark noise-overlay flex items-center">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative w-full max-w-md mx-auto px-4 py-32">
          <div className="rounded-lg border border-white/10 bg-pine/70 backdrop-blur-xl p-8 sm:p-10">
            <div className="flex items-center gap-3">
              <img src={ASSETS.logo} alt={`${COMPANY.name} logo`} className="h-10 w-10 rounded-md ring-1 ring-white/15" />
              <div>
                <h1 className="font-display text-xl font-bold tracking-tight text-white">Admin Access</h1>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-volt/70">Restricted — BNM team only</p>
              </div>
            </div>
            <form onSubmit={onSubmit} data-testid="admin-login-form" className="mt-8 space-y-5">
              <div>
                <Label htmlFor="admin-email" className="text-slate-300">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-email-input"
                  className="mt-1.5 h-12 bg-obsidian/60 border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-volt"
                />
              </div>
              <div>
                <Label htmlFor="admin-password" className="text-slate-300">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  className="mt-1.5 h-12 bg-obsidian/60 border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-volt"
                />
              </div>
              {error && (
                <p role="alert" data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="admin-login-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-volt px-6 py-3.5 font-semibold text-obsidian transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-volt-soft hover:text-obsidian disabled:opacity-60 disabled:hover:translate-y-0"
              >
                <Lock className="h-4 w-4" aria-hidden="true" />
                {submitting ? "Signing in…" : "Sign In"}
              </button>
            </form>
            <Link to="/" data-testid="admin-login-back" className="mt-6 block text-center text-xs text-slate-500 hover:text-volt transition-colors">
              ← Back to website
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
